import {RoleGrantKey} from './rolePolicyBuilder';

/**
 * FNP-1365 — "ASKED, DECLINED", REMEMBERED.
 *
 * The ticket is explicit that a customer who declined once must not be prompted
 * on every subsequent delete. An elevation prompt that reappears every time is
 * not an invitation, it is nagging, and it converts a feature meant to build
 * trust into the thing people click past without reading.
 *
 * ⚠️ WHERE THIS LIVES, AND WHY IT IS NOT ON THE CONNECTION (FNP-1365 open
 * question 1, decided here).
 *
 * There is no field for it. `ProviderConnection` carries the catalog
 * capabilities, the declared `dnsCapabilities`, the status and the secret ref,
 * and `DeclareProviderCapabilitiesInput` accepts only `dns` — verified against
 * `types.ts` and `mutations.ts`. Adding one would mean a schema change, a
 * resolver, a migration and a mutation, for a nudge.
 *
 * And it would be modelling the wrong thing. Everything the connection stores
 * is a statement about the customer's AWS account: what the role is permitted
 * to do. "Do not offer me this prompt again" is a statement about a person's
 * preference in a browser. Those are different facts with different lifetimes,
 * and putting the second in the record that governs the first means a UI
 * preference is now something the backend has an opinion about.
 *
 * So: `localStorage`, per connection and per grant. What that buys and what it
 * costs, stated plainly rather than discovered later:
 *
 *   ✓ No backend surface at all, which is what "build only what is requested"
 *     asks for when the alternative is inventing an API for a dismissal.
 *   ✓ Survives reloads and navigation, which is the whole requirement.
 *   ✗ Per browser. The same person on their laptop and their phone is asked
 *     once on each. That is a mild re-ask, not a broken promise — and the
 *     honest read is that a prompt they have not seen in this browser is one
 *     worth showing once.
 *   ✗ Cleared with site data.
 *
 * ⚠️ A DECLINE IS NOT A REFUSAL OF THE TASK, and nothing here should be read as
 * one. The customer is still shown how to do it themselves in AWS — see the
 * prompt. This only records that they do not want to be ASKED again.
 *
 * ⚠️ EVERY ACCESS IS WRAPPED. Private windows, blocked site data and storage
 * quotas all throw on access rather than returning empty, and a delete button
 * that throws because a dismissal could not be recorded is a far worse defect
 * than a prompt shown twice. Every failure resolves to "not declined", which
 * shows the prompt — the recoverable direction.
 */

const STORAGE_PREFIX = 'flexi.elevation.declined';

/**
 * ⚠️ THE CONNECTION ID IS PART OF THE KEY, and it has to be. Grants are
 * per-connection: a tenant may hold a writable Route 53 connection and a
 * read-only one at the same time, and declining to widen one says nothing about
 * the other. A key on the grant alone would silence a prompt the customer has
 * never seen for the connection they are actually looking at.
 */
const declineKey = (connectionId: string, grant: RoleGrantKey): string =>
    `${STORAGE_PREFIX}.${connectionId}.${grant}`;

export const hasDeclinedElevation = (
    connectionId: string,
    grant: RoleGrantKey,
): boolean => {
    try {
        return window.localStorage.getItem(declineKey(connectionId, grant)) !== null;
    } catch {
        // Storage unreadable. Ask again rather than suppressing a prompt on the
        // strength of a failure — a repeated offer is recoverable, a silently
        // withheld one is not.
        return false;
    }
};

export const recordElevationDecline = (
    connectionId: string,
    grant: RoleGrantKey,
): void => {
    try {
        // The timestamp is not read by anything today. It is stored because a
        // bare marker tells a future reader nothing about when the customer
        // decided, and "declined eight months ago" is a different fact from
        // "declined this morning" if this ever grows an expiry.
        window.localStorage.setItem(
            declineKey(connectionId, grant),
            new Date().toISOString(),
        );
    } catch {
        // Could not record it. The customer will be asked again next time,
        // which is the mild failure — never let this break the delete flow they
        // were actually in the middle of.
    }
};

/**
 * Forget a decline.
 *
 * ⚠️ CALLED WHEN THE GRANT IS ACTUALLY MADE, so a customer who declines, then
 * changes their mind and widens the role, then later tightens it again is asked
 * afresh. A stale decline from months earlier would otherwise silence the
 * prompt forever on a connection whose whole history has since changed.
 */
export const clearElevationDecline = (
    connectionId: string,
    grant: RoleGrantKey,
): void => {
    try {
        window.localStorage.removeItem(declineKey(connectionId, grant));
    } catch {
        // Nothing to do, and nothing worth interrupting the customer over.
    }
};
